import { useState } from "react";
import { Info } from "lucide-react";
import { managementApi } from "../../api";
import type { ManagementItem, ManagementSummary } from "../../api";
import { useAsync, useConfirm } from "../../hooks";
import { FilterField, LoadingText, Select } from "../ui";
import { describeCron, formatCurrency, formatDateTime, formatNumber, statusBadgeClass } from "../../utils";

function Cadence({ value }: { value: string | null }) {
  if (!value) return <span className="text-gray-400">—</span>;
  const human = describeCron(value);
  if (!human) return <span>{value}</span>;
  return (
    <span className="inline-flex items-center gap-1">
      {human}
      <span title={`Cron: ${value}`} className="cursor-help text-gray-400">
        <Info size={12} />
      </span>
    </span>
  );
}

const TYPE_LABELS: Record<string, string> = {
  report_schedule: "Scheduled report",
  scheduled_dashboard: "Scheduled dashboard",
  alert: "Alert",
  monitored_url: "Monitored URL",
  user_report_schedule: "User report schedule",
};

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "failed", label: "Failed" },
];

const PERIOD_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

export default function ManagementView() {
  const [status, setStatus] = useState("");
  const [days, setDays] = useState("30");
  const { confirm, confirmDialog } = useConfirm();

  const { data, loading, error, reload } = useAsync<ManagementItem[]>(
    () => managementApi.getItems({ status: status || undefined, days: Number(days) }).then((r) => r.data),
    [status, days],
    { errorMessage: "Failed to load managed items." },
  );

  const { data: summary } = useAsync<ManagementSummary>(
    () => managementApi.getSummary(Number(days)).then((r) => r.data),
    [days],
    { errorMessage: "Failed to load usage." },
  );

  const items = data ?? [];

  async function togglePause(item: ManagementItem) {
    if (item.status !== "paused") {
      const ok = await confirm({
        title: "Pause item",
        message: `Pause "${item.name}"? It will stop running until resumed.`,
        confirmLabel: "Pause",
        danger: true,
      });
      if (!ok) return;
      await managementApi.setActive(item.type, item.id, false);
    } else {
      await managementApi.setActive(item.type, item.id, true);
    }
    reload();
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Brian usage (account) — {summary ? `last ${summary.period_days} days` : "…"}
          </p>
          <p className="mt-1 text-lg font-bold text-gray-900">
            {summary ? formatNumber(summary.total_credits) : "—"}{" "}
            <span className="text-sm font-normal text-gray-500">credits</span>
            <span className="mx-2 text-gray-300">·</span>
            {summary ? formatCurrency(summary.total_cost_gbp) : "—"}
          </p>
          {summary && (
            <p className="mt-0.5 text-[11px] text-gray-400">
              Chat {formatCurrency(summary.chat_cost_gbp)}
              <span className="mx-1.5 text-gray-300">·</span>
              Scheduled {formatCurrency(summary.scheduled_cost_gbp)}
            </p>
          )}
        </div>
        <FilterField label="Period">
          <Select value={days} onChange={(e) => setDays(e.target.value)} className="h-9 w-40">
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </FilterField>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-3">
        <FilterField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 w-48">
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </FilterField>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        {loading ? (
          <LoadingText className="px-4 py-6" />
        ) : error ? (
          <p className="px-4 py-6 text-sm text-red-500">{error}</p>
        ) : items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400">No items match this filter.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2 font-semibold">Type</th>
                <th className="px-4 py-2 font-semibold">Name</th>
                <th className="px-4 py-2 font-semibold">Owner</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold">Cadence</th>
                <th className="px-4 py-2 font-semibold">Last run</th>
                <th className="px-4 py-2 text-right font-semibold">Credits</th>
                <th className="px-4 py-2 text-right font-semibold">Cost</th>
                <th className="px-4 py-2 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr key={`${item.type}:${item.id}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-500">{TYPE_LABELS[item.type] ?? item.type}</td>
                  <td className="px-4 py-2.5 text-gray-800">
                    {item.name}
                    {item.note && <span className="ml-2 text-[11px] text-amber-600">({item.note})</span>}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{item.owner_name ?? "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500"><Cadence value={item.cadence} /></td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">
                    {item.last_run_at ? formatDateTime(item.last_run_at) : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">
                    {item.credits != null ? formatNumber(item.credits) : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">
                    {item.cost_gbp != null ? formatCurrency(item.cost_gbp) : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      onClick={() => togglePause(item)}
                      className={`rounded-lg border px-3 py-1 text-xs font-medium ${
                        item.status === "paused"
                          ? "border-green-200 text-green-700 hover:bg-green-50"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {item.status === "paused" ? "Resume" : "Pause"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirmDialog}
    </div>
  );
}
